import {
  schEmptyString,
  schStateCode,
  schUserDefinedRoles,
  userDefinedRoleToLabel,
} from '@aloft/toolkit-shared';
import { Button, Flex, SimpleInput, SimpleSelect } from '@aloft/toolkit-sui';

import { Controller, SubmitHandler } from 'react-hook-form';
import { z } from 'zod';

import { useToaster } from '../../hooks/useToaster/useToaster.js';
import { useZodForm } from '../../hooks/useZodForm.js';
import { AccountModuleDeps } from './types.js';

const schStringOrEmpty = z.string().or(schEmptyString);

const schPersonalInformationForm = z.object({
  firstName: schStringOrEmpty,
  lastName: schStringOrEmpty,
  company: schStringOrEmpty,
  role: schUserDefinedRoles.or(schEmptyString),
  licenseState: schStateCode.or(schEmptyString),
  licenseID: schStringOrEmpty,
});
type PersonalInformationForm = z.infer<typeof schPersonalInformationForm>;

type Props = {
  account: AccountModuleDeps['account'];
  updateAccount: AccountModuleDeps['updateAccount'];
  isUpdatingAccount: AccountModuleDeps['isUpdatingAccount'];
};

export function PersonalInformationContent({ account, updateAccount, isUpdatingAccount }: Props) {
  const makeToast = useToaster();

  const form = useZodForm({
    defaultValues: schPersonalInformationForm.parse({
      firstName: account.firstName ?? '',
      lastName: account.lastName ?? '',
      company: account.company ?? '',
      role: account.userDefinedRole ?? '',
      licenseState: account.licenseState ?? '',
      licenseID: account.licenseID ?? '',
    }),
    schema: schPersonalInformationForm,
  });

  const onSubmit: SubmitHandler<PersonalInformationForm> = async (data) => {
    await updateAccount({
      ...account,
      firstName: data.firstName || undefined,
      lastName: data.lastName || undefined,
      company: data.company || undefined,
      userDefinedRole: data.role || undefined,
      licenseState: data.licenseState || undefined,
      licenseID: data.licenseID || undefined,
    });
    makeToast({
      id: 'update-profile-success',
      title: 'Profile information saved',
      type: 'success',
    });
    form.reset(data);
  };

  const {
    formState: { errors, isDirty },
  } = form;

  return (
    <form
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      }}
    >
      <Controller
        name="firstName"
        control={form.control}
        render={({ field }) => (
          <SimpleInput {...field} label="First name" invalid={!!errors.firstName} />
        )}
      />
      <Controller
        name="lastName"
        control={form.control}
        render={({ field }) => (
          <SimpleInput {...field} label="Last name" invalid={!!errors.lastName} />
        )}
      />
      <Controller
        name="company"
        control={form.control}
        render={({ field }) => (
          <SimpleInput {...field} label="Company" invalid={!!errors.company} />
        )}
      />
      <SimpleSelect {...form.register('role')} label="Role" invalid={!!errors.role}>
        <option value="">Select a role</option>
        {schUserDefinedRoles.options.map((role) => (
          <option key={role} value={role}>
            {userDefinedRoleToLabel[role]}
          </option>
        ))}
      </SimpleSelect>

      <Controller
        name="licenseID"
        control={form.control}
        render={({ field }) => (
          <SimpleInput {...field} label="License ID" invalid={!!errors.licenseID} />
        )}
      />
      <SimpleSelect
        {...form.register('licenseState')}
        label="License state"
        invalid={!!errors.licenseState}
      >
        <option value="">Select a state</option>
        {schStateCode.options.map((stateCode) => (
          <option key={stateCode} value={stateCode}>
            {stateCode}
          </option>
        ))}
      </SimpleSelect>
      <Flex alignSelf="flex-end" gap="6px">
        <Button
          text="Cancel"
          onClick={() => form.reset()}
          disabled={!isDirty}
          variant="secondary"
        />
        <Button
          text="Save"
          onClick={form.handleSubmit(onSubmit)}
          disabled={!isDirty || isUpdatingAccount}
          isLoading={isUpdatingAccount}
        />
      </Flex>
    </form>
  );
}
